/**
 * Phase 11 C — Relative-time ladder shared by both notification bells and
 * any other "how long ago" surfaces.
 *
 *   < 1 minute        → "just now"
 *   1 – 59 min        → "X min ago"
 *   1 – 23 hr         → "X hr ago"
 *   1 day             → "yesterday"
 *   2 – 29 days       → "X days ago"
 *   30 days – 12 mo   → "X months ago"
 *   ≥ 12 months       → "X yr [Y months] ago"
 *
 * Accepts anything the Date constructor accepts (ISO string, epoch ms, Date).
 * Returns "—" for null/undefined/invalid input so callers can render straight.
 */
export function relativeTime(input) {
    if (input == null || input === "") return "—";
    const then = input instanceof Date ? input : new Date(input);
    const t = then.getTime();
    if (Number.isNaN(t)) return "—";

    const diffMs = Date.now() - t;
    // Future timestamps → "just now" (clock skew is not a UX problem to expose).
    if (diffMs < 60_000) return "just now";

    const minutes = Math.floor(diffMs / 60_000);
    if (minutes < 60) return `${minutes} min ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr ago`;

    const days = Math.floor(hours / 24);
    if (days === 1) return "yesterday";
    if (days < 30) return `${days} days ago`;

    // Approximation is fine — nobody cares whether a 2-month-old note is
    // 61 or 63 days. 30.4375 = average days per month over 4 years.
    const months = Math.floor(days / 30.4375);
    if (months < 12) return `${months} months ago`;

    const years = Math.floor(months / 12);
    const remMonths = months % 12;
    if (remMonths === 0) return `${years} yr ago`;
    return `${years} yr ${remMonths} months ago`;
}

/** Human-friendly absolute timestamp, kept as a hover-title companion. */
export function absoluteTitle(input) {
    if (!input) return "";
    const d = input instanceof Date ? input : new Date(input);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
}
